import type { DeckStudyCard } from "@-/dev.improve.haniverse.lib.cards";
import type { DeckSortMode } from "@-/dev.improve.haniverse.routes.nav.app.deck.entities";

function dueMs(studyCard: DeckStudyCard): number {
  const due = studyCard.fsrsCard?.due;
  return due == null ? Number.POSITIVE_INFINITY : new Date(due).getTime();
}

function lastReviewMs(studyCard: DeckStudyCard): number {
  const lastReview = studyCard.fsrsCard?.lastReview;
  return lastReview == null ? 0 : new Date(lastReview).getTime();
}

function startedStudyingMs(studyCard: DeckStudyCard): number {
  return new Date(studyCard.startedStudyingAt ?? studyCard.createdAt).getTime();
}

/**
 * The deck's sort order, decided entirely from the actor rows (FSRS schedule + card timestamps)
 * — no content/static-DB lookup is needed to order the deck, which is what lets the first paint
 * come from the local actor DB alone.
 *
 * - `due-soonest`: unscheduled cards last.
 * - `recently-studied`: most recent review first; never-reviewed cards last.
 * - `most-familiar`: highest FSRS stability first, reps as the tiebreak.
 * - `newest` (default): most recently started first.
 */
export function compareDeckStudyCards(
  sortMode: DeckSortMode,
  a: DeckStudyCard,
  b: DeckStudyCard,
): number {
  if (sortMode === "due-soonest") {
    return dueMs(a) - dueMs(b);
  }
  if (sortMode === "recently-studied") {
    return lastReviewMs(b) - lastReviewMs(a);
  }
  if (sortMode === "most-familiar") {
    const aStability = a.fsrsCard?.stability ?? 0;
    const bStability = b.fsrsCard?.stability ?? 0;
    if (bStability !== aStability) {
      return bStability - aStability;
    }
    return (b.fsrsCard?.reps ?? 0) - (a.fsrsCard?.reps ?? 0);
  }
  return startedStudyingMs(b) - startedStudyingMs(a);
}
